import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';

import { DistrictProcessingStatistic, DistrictProcessingStatisticSummary } from '@/interfaces';
import { DistrictProcessingStatisticsService } from '@/service';

interface PipelineChartSegment {
    key: string;
    label: string;
    value: number;
    color: string;
}

interface PipelineYearChart {
    year: number;
    row: DistrictProcessingStatistic;
    chartData: Record<string, unknown> | null;
    chartOptions: Record<string, unknown> | null;
    segments: PipelineChartSegment[];
    hasPipelineCounts: boolean;
}

@Component({
    selector: 'app-statistics-summary-charts',
    standalone: true,
    imports: [CommonModule, FormsModule, ChartModule, SelectModule, Tooltip],
    templateUrl: './summary-charts.component.html'
})
export class StatisticsSummaryChartsComponent implements OnInit, OnChanges {
    @Input() summary: DistrictProcessingStatisticSummary | null = null;
    @Input() loading = false;

    stateOptions: { label: string; value: string }[] = [];
    selectedState: string | null = null;
    yearCharts: PipelineYearChart[] = [];
    detailLoading = false;

    constructor(private service: DistrictProcessingStatisticsService) {}

    ngOnInit(): void {
        this.service.options().subscribe({
            next: (options) => {
                this.stateOptions = options.states.map((state) => ({ label: state, value: state }));

                if (!this.selectedState && this.stateOptions.length) {
                    this.selectedState = this.stateOptions[0].value;
                }

                this.loadStateCharts();
            }
        });
    }

    ngOnChanges(): void {
        // Overview cards only; charts use their own state selector.
    }

    filterHint(): string {
        if (!this.summary) {
            return '';
        }

        const parts: string[] = [];
        if (this.summary.filters.states.length) {
            parts.push(`States: ${this.summary.filters.states.join(', ')}`);
        }
        if (this.summary.filters.years.length) {
            parts.push(`Years: ${this.summary.filters.years.join(', ')}`);
        }

        return parts.length ? parts.join(' · ') : 'All states · all years in pipeline history';
    }

    yearChartsRowClass(): string {
        return this.yearCharts.length > 3 ? 'flex gap-3 w-full overflow-x-auto flex-nowrap pb-1' : 'grid';
    }

    yearCardWrapperClass(): string {
        if (this.yearCharts.length > 3) {
            return 'flex-shrink-0 col-4';
        }

        const count = this.yearCharts.length;
        if (count === 1) {
            return 'col-12 flex justify-content-center';
        }
        if (count === 2) {
            return 'col-12 md:col-6';
        }

        return 'col-12 md:col-4';
    }

    yearCardClass(): string {
        const base = 'flex flex-column align-items-center gap-3 p-3 border-1 surface-border border-round';

        if (this.yearCharts.length === 1) {
            return `${base} w-20rem`;
        }

        return `${base} w-full h-full`;
    }

    onStateChange(): void {
        this.loadStateCharts();
    }

    private loadStateCharts(): void {
        if (!this.selectedState) {
            this.yearCharts = [];
            return;
        }

        this.detailLoading = true;
        this.service.rowsForState(this.selectedState).subscribe({
            next: (rows) => {
                this.yearCharts = rows.map((row) => this.buildYearChart(row));
                this.detailLoading = false;
            },
            error: () => {
                this.yearCharts = [];
                this.detailLoading = false;
            }
        });
    }

    private buildYearChart(row: DistrictProcessingStatistic): PipelineYearChart {
        const hasPipelineCounts = this.hasPipelineCountsForRow(row);

        if (!hasPipelineCounts) {
            return {
                year: row.year,
                row,
                chartData: null,
                chartOptions: null,
                segments: [],
                hasPipelineCounts: false
            };
        }

        const withAgenda = row.districts_with_agenda_url;
        const chartBase = withAgenda > 0 ? withAgenda : row.total_districts;
        const analyzed = row.districts_analyzed ?? 0;
        const crawled = row.districts_crawled ?? 0;
        const crawledOnly = Math.max(crawled - analyzed, 0);
        const notCrawled = Math.max(withAgenda - crawled, 0);

        const segments: PipelineChartSegment[] = [
            {
                key: 'analyzed',
                label: 'Analyzed',
                value: analyzed,
                color: '#a855f7'
            },
            {
                key: 'crawled_only',
                label: 'Crawled only (w/o files)',
                value: crawledOnly,
                color: '#22c55e'
            },
            {
                key: 'not_crawled',
                label: 'Not crawled',
                value: notCrawled,
                color: '#e2e8f0'
            }
        ].filter((segment) => segment.value > 0);

        if (!segments.length) {
            return {
                year: row.year,
                row,
                chartData: null,
                chartOptions: null,
                segments: [],
                hasPipelineCounts: true
            };
        }

        const chartData = {
            labels: segments.map((segment) => segment.label),
            datasets: [
                {
                    data: segments.map((segment) => segment.value),
                    backgroundColor: segments.map((segment) => segment.color),
                    borderWidth: 0,
                    hoverOffset: 6
                }
            ]
        };

        const chartOptions = {
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: { parsed: number; label?: string }) => {
                            const pct = chartBase > 0 ? ((ctx.parsed / chartBase) * 100).toFixed(1) : '0.0';

                            return `${ctx.label}: ${ctx.parsed.toLocaleString()} (${pct}%)`;
                        },
                    }
                }
            },
            maintainAspectRatio: false
        };

        return {
            year: row.year,
            row,
            chartData,
            chartOptions,
            segments,
            hasPipelineCounts: true
        };
    }

    private hasPipelineCountsForRow(row: DistrictProcessingStatistic): boolean {
        return row.districts_crawled !== null || row.districts_analyzed !== null || row.districts_without_meetings > 0;
    }
}
